/*
  FD brand: the mobile colour strip.

  A horizontally scrollable row of swatch squares under the gallery (fabric
  tab pills above it on dual-fabric products) plus a fabric+colour summary row
  beside the basket button. Mobile/tablet only — CSS keeps all of it
  display:none at >=980px, where the full colour grid and fabric drawer stay
  exactly as they were.

  The strip owns NO state. Every tap proxies to the real radios in the colour
  fieldset (mobile-hidden, never removed): a swatch tap clicks its radio by
  data-option-value-id; a fabric tab clicks that fabric's first usable radio —
  the exact commit fd-fabric-drawer.js uses. Everything downstream (variant id,
  price, gallery filter, caption, URL, resolver) is Dawn's own change path.

  Like the drawer, this element lives outside <variant-selects> and repaints
  itself from the re-rendered radios via a MutationObserver; the singleton
  fieldset guard covers Dawn's view-transition window where two pickers exist.

  Progressive enhancement: the strip ships `hidden` and the grid-hiding CSS
  keys on the `fd-strip-on` class this script sets — with JS off, mobile keeps
  the full grid and never sees the strip.
*/
(function () {
  'use strict';

  if (customElements.get('fd-colour-strip')) return;

  customElements.define(
    'fd-colour-strip',
    class FdColourStrip extends HTMLElement {
      connectedCallback() {
        this.root = this.closest('product-info');
        if (!this.root) return;
        this.scope = this.root.querySelector('fd-option-resolver') || this.root;

        this.selectedEl = this.querySelector('[data-fd-strip-selected]');
        this.countEl = this.querySelector('[data-fd-strip-count]');
        this.sumRow = this.root.querySelector('[data-fd-sumrow]');
        this.sumThumb = this.root.querySelector('[data-fd-sumrow-thumb]');
        this.sumText = this.root.querySelector('[data-fd-sumrow-text]');
        this.sumChange = this.root.querySelector('[data-fd-sumrow-change]');

        this.separator = this.dataset.separator || '';
        this.countTemplate = this.dataset.countTemplate || '{count} colours';

        this.addEventListener('click', (e) => this.onTap(e));
        if (this.sumChange) {
          this.sumChange.addEventListener('click', () => {
            this.scrollIntoView({ behavior: 'smooth', block: 'center' });
          });
        }

        this.observer = new MutationObserver(() => this.schedule());
        this.observer.observe(this.scope, { childList: true, subtree: true });

        // Init gate: strip + summary become visible, grid/drawer hide (CSS).
        this.removeAttribute('hidden');
        if (this.sumRow) this.sumRow.removeAttribute('hidden');
        this.root.classList.add('fd-strip-on');
        this.schedule();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        clearTimeout(this.queuedTimer);
        this.queued = false;
        clearTimeout(this.paintRetry);
      }

      schedule() {
        if (this.queued) return;
        this.queued = true;
        this.paintTries = 0;
        this.queuedTimer = window.setTimeout(() => {
          this.queued = false;
          this.paint();
        }, 0);
      }

      /* Singleton guard: Dawn's view transition briefly leaves the outgoing
         and incoming pickers in the DOM together. */
      fieldset() {
        var found = this.scope.querySelectorAll('[data-fd-colour-option]');
        if (found.length !== 1) return null;
        return found[0];
      }

      onTap(e) {
        var tab = e.target.closest('[data-fd-strip-tab]');
        if (tab) {
          this.commitFabric(tab.dataset.fdStripTab);
          return;
        }
        var sw = e.target.closest('[data-fd-strip-value-id]');
        if (sw) this.commitColour(sw.dataset.fdStripValueId);
      }

      commitColour(valueId) {
        var fieldset = this.fieldset();
        if (!fieldset) return;
        var input = fieldset.querySelector(
          'input[type="radio"][data-option-value-id="' + valueId + '"]'
        );
        if (input && !input.checked) input.click();
      }

      /* The drawer's exact commit: a fabric's landing colour is its first
         orderable one; a fabric with none is still clickable so the shopper
         gets Dawn's own unavailable state rather than a silent no-op. */
      commitFabric(name) {
        var fieldset = this.fieldset();
        if (!fieldset) return;
        /* Re-tapping the fabric already on screen is a no-op, exactly as the
           drawer's commit() opens: resolving the first orderable radio again
           would throw away a colour the shopper picked further down the row. */
        var checked = fieldset.querySelector('input[type="radio"]:checked');
        var current = checked ? checked.closest('.fd-swgroup') : null;
        if (current && current.dataset.fdGroup === name) return;
        var target = Array.prototype.find.call(
          fieldset.querySelectorAll('.fd-swgroup'),
          function (g) { return g.dataset.fdGroup === name; }
        );
        if (!target) return;
        var input =
          target.querySelector('input[type="radio"]:not(.disabled)') ||
          target.querySelector('input[type="radio"]');
        if (input && !input.checked) input.click();
      }

      paint() {
        var fieldset = this.fieldset();
        if (!fieldset) {
          if (!this.isConnected) return;
          this.paintTries = (this.paintTries || 0) + 1;
          if (this.paintTries > 50) return;
          clearTimeout(this.paintRetry);
          this.paintRetry = window.setTimeout(() => this.paint(), 60);
          return;
        }
        this.paintTries = 0;
        clearTimeout(this.paintRetry);

        var checked = fieldset.querySelector('input[type="radio"]:checked');
        if (!checked) return;
        var checkedId = checked.dataset.optionValueId;
        var groupEl = checked.closest('.fd-swgroup');
        var groupName = groupEl ? groupEl.dataset.fdGroup : '';

        // Availability + selection mirrored from the radios, never guessed.
        var offById = {};
        fieldset.querySelectorAll('input[type="radio"][data-option-value-id]').forEach(function (r) {
          offById[r.dataset.optionValueId] = r.classList.contains('disabled');
        });

        var selectedBtn = null;
        this.querySelectorAll('[data-fd-strip-value-id]').forEach(function (btn) {
          var id = btn.dataset.fdStripValueId;
          var isSel = id === checkedId;
          btn.setAttribute('aria-pressed', String(isSel));
          btn.classList.toggle('fd-strip__sw--off', Boolean(offById[id]));
          if (isSel) selectedBtn = btn;
        });

        // One visible fabric group at a time (tabs switch it by committing).
        var visibleCount = 0;
        this.querySelectorAll('[data-fd-strip-group]').forEach(function (g) {
          var on = !groupName || g.dataset.fdStripGroup === groupName;
          g.classList.toggle('fd-strip__group--sel', on);
          if (on) visibleCount = g.querySelectorAll('[data-fd-strip-value-id]').length;
        });
        this.querySelectorAll('[data-fd-strip-tab]').forEach(function (t) {
          t.setAttribute('aria-pressed', String(t.dataset.fdStripTab === groupName));
        });

        var value = checked.value;
        var short = value;
        if (this.separator && value.indexOf(this.separator) !== -1) {
          var parts = value.split(this.separator);
          short = parts[parts.length - 1].trim();
        }
        if (this.selectedEl) this.selectedEl.textContent = short;
        if (this.countEl) {
          this.countEl.textContent = this.countTemplate.replace('{count}', String(visibleCount));
        }

        // Summary row: the thumb is the selected radio's own swatch, cloned —
        // no second lookup, no chance of divergence (drawer precedent).
        if (this.sumText) this.sumText.textContent = value;
        if (this.sumThumb) {
          var box = checked.parentElement.querySelector('.fd-sw__box');
          if (box) {
            this.sumThumb.classList.toggle('fd-sumrow__thumb--fill', box.classList.contains('fd-sw__box--fill'));
            this.sumThumb.classList.toggle('fd-sumrow__thumb--img', box.classList.contains('fd-sw__box--img'));
            this.sumThumb.setAttribute('style', box.getAttribute('style') || '');
            /* The dashed placeholder is TEXT in the grid's box (its real name,
               never an invented fill), so the clone must carry it too or an
               unphotographed colour lands here as a bare dashed square. The
               box's only other child is the tick svg, which contributes no
               text, so a photographed swatch still trims to ''. */
            this.sumThumb.textContent = box.textContent.trim();
          }
        }

        /* Repaints fire on every mutation inside the resolver, most of which
           have nothing to do with the colour. Scrolling then would nudge the
           strip under the shopper's thumb mid-scroll, so the scroll is tied to
           an actual change of selection.

           The first paint is a change too (nothing was known before it), but
           scrollIntoView there would scroll the PAGE to a strip the shopper
           has not reached yet -- a deep link to a colour halfway down the row
           would jump the viewport on load. So the first paint centres the
           swatch inside its own scroller and touches nothing else. */
        var first = this.lastCheckedId === undefined;
        var changed = checkedId !== this.lastCheckedId;
        this.lastCheckedId = checkedId;

        if (!selectedBtn) return;
        if (first) {
          var scroller = selectedBtn.closest('.fd-strip__scroll');
          if (scroller) {
            var btnBox = selectedBtn.getBoundingClientRect();
            var boxBox = scroller.getBoundingClientRect();
            scroller.scrollLeft +=
              btnBox.left - boxBox.left - (scroller.clientWidth - btnBox.width) / 2;
          }
          return;
        }
        if (changed && selectedBtn.scrollIntoView) {
          selectedBtn.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        }
      }
    }
  );
})();
